import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Alert,
  Divider,
  useTheme,
  Grid,
  Chip,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TerminalIcon from "@mui/icons-material/Terminal";
import TopicIcon from "@mui/icons-material/Topic";
import axiosInstance from "../../api/axiosInstance";

export default function BulkHtmlImport() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    success: "#00e676",
    info: "#00b0ff",
  };

  const fileInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [detectedSubjects, setDetectedSubjects] = useState({});
  const [videoLinkDefault, setVideoLinkDefault] = useState("NA");
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const htmlFiles = files.filter((file) => file.name.endsWith(".html"));

    if (htmlFiles.length === 0) {
      alert("No HTML files found in the selected folder.");
      return;
    }

    setSelectedFiles(htmlFiles);

    // Extract root folder name
    const firstPath = htmlFiles[0].webkitRelativePath || "";
    const firstPathClean = firstPath.replace(/\\/g, "/");
    const parts = firstPathClean.split("/");
    if (parts.length > 0) {
      setFolderName(parts[0]);
    } else {
      setFolderName("Selected Folder");
    }

    // Group files by subject folder
    const subjectsMap = {};
    htmlFiles.forEach((file) => {
      const pathClean = file.webkitRelativePath.replace(/\\/g, "/");
      const pathParts = pathClean.split("/").filter(Boolean);
      let subject = "default";
      if (pathParts.length >= 3) {
        subject = pathParts[1];
      } else if (pathParts.length === 2) {
        subject = pathParts[0];
      }
      subjectsMap[subject] = (subjectsMap[subject] || 0) + 1;
    });

    setDetectedSubjects(subjectsMap);
    setStatusMsg(null);
    setErrorMsg(null);
    setLogs([]);
  };

  const triggerFolderPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Please select a local folder first.");
      return;
    }

    setLoading(true);
    setStatusMsg(null);
    setErrorMsg(null);
    setLogs([]);

    const formData = new FormData();
    formData.append("video_link_default", videoLinkDefault || "NA");

    selectedFiles.forEach((file) => {
      formData.append("files", file);
      // Append webkitRelativePath as a parallel array
      formData.append("paths", file.webkitRelativePath);
    });

    try {
      const res = await axiosInstance.post("/post/admin/import-local-folder", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setStatusMsg(res.data.message || "Import completed successfully!");
      setLogs(res.data.log || []);
    } catch (err) {
      console.error("Error importing folder:", err);
      setErrorMsg(
        err.response?.data?.detail || "An error occurred during the bulk import process."
      );
      if (err.response?.data?.log) {
        setLogs(err.response.data.log);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Bulk HTML Content Import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Select a local content folder from your computer. The system parses your folder recursively, groups lessons by subject, deletes old records, and saves the new content pages.
      </Typography>

      <Grid container spacing={4}>
        {/* Left Side: Controls & Folder Picker */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              background: isDark ? "rgba(255,255,255,0.01)" : "#fdfdfd",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Hidden Input for Folder Selection */}
            <input
              type="file"
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={handleFolderSelect}
              style={{ display: "none" }}
              ref={fileInputRef}
            />

            <Stack spacing={3} sx={{ height: "100%", justifyContent: "space-between" }}>
              <Box>
                {/* Upload Area Dropzone Design */}
                <Box
                  onClick={triggerFolderPicker}
                  sx={{
                    border: `2px dashed ${selectedFiles.length > 0 ? colors.success : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                    borderRadius: "16px",
                    p: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    background: isDark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)",
                    "&:hover": {
                      borderColor: colors.info,
                      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                    },
                    mb: 3,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: selectedFiles.length > 0 ? colors.success : "text.secondary", mb: 2 }} />
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {selectedFiles.length > 0 ? "Folder Loaded" : "Choose Local Folder"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFiles.length > 0
                      ? `"${folderName}" loaded with ${selectedFiles.length} HTML files.`
                      : "Click to select a directory recursively."}
                  </Typography>
                </Box>

                {/* Selected Folder Metadata Details */}
                {selectedFiles.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      background: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                      mb: 3,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <FolderIcon sx={{ color: colors.info }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Folder: {folderName}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Detected Subjects & Page Counts:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {Object.entries(detectedSubjects).map(([subject, count]) => (
                        <Chip
                          key={subject}
                          icon={<TopicIcon />}
                          label={`${subject}: ${count} ${count === 1 ? "page" : "pages"}`}
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: "8px" }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                )}

                {/* Default Video Link Input */}
                <TextField
                  label="Default Video Link (NA if not available)"
                  required
                  fullWidth
                  size="small"
                  value={videoLinkDefault}
                  onChange={(e) => setVideoLinkDefault(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Box>

              <Box>
                <Divider sx={{ my: 2 }} />

                <Button
                  onClick={handleImport}
                  variant="contained"
                  disabled={loading || selectedFiles.length === 0}
                  fullWidth
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  sx={{
                    borderRadius: "10px",
                    fontWeight: 700,
                    py: 1.2,
                    textTransform: "none",
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    boxShadow: "0 4px 15px rgba(229, 9, 20, 0.2)",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {loading ? "Running Import..." : "Run Folder Import"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Right Side: Log Window / Terminal Console */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              background: isDark ? "rgba(255,255,255,0.01)" : "#fdfdfd",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TerminalIcon sx={{ color: isDark ? "#fff" : "#333" }} />
              <Typography variant="h6" fontWeight={700}>
                Execution Log
              </Typography>
            </Stack>

            {/* Feedback alerts */}
            {statusMsg && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: "10px" }}>
                {statusMsg}
              </Alert>
            )}

            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                {errorMsg}
              </Alert>
            )}

            {/* Terminal Console */}
            <Box
              sx={{
                flexGrow: 1,
                background: "#0d0d0d",
                color: colors.success,
                borderRadius: "12px",
                p: 2,
                fontFamily: "Consolas, Monaco, 'Courier New', Courier, monospace",
                fontSize: "0.85rem",
                minHeight: 350,
                maxHeight: 500,
                overflowY: "auto",
                boxShadow: "inset 0 0 10px rgba(0,0,0,0.8)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {logs.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "inherit",
                    color: "rgba(255,255,255,0.3)",
                    fontStyle: "italic",
                  }}
                >
                  No active execution. Select a local folder containing HTML files and click "Run Folder Import" to start.
                </Typography>
              ) : (
                logs.map((logLine, idx) => {
                  let style = { marginBottom: "4px" };
                  if (logLine.includes("❌")) {
                    style.color = "#ff1744";
                  } else if (logLine.includes("🗑️")) {
                    style.color = "#ff9100";
                  } else if (logLine.includes("✅")) {
                    style.color = colors.success;
                  }
                  return (
                    <div key={idx} style={style}>
                      {logLine}
                    </div>
                  );
                })
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
